import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
// The Emergent OAuth flow redirects back with #session_id=... in the URL hash.
// This component reads it once, exchanges it for a session cookie, then routes to /dashboard.
const AuthCallback = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Parse #session_id=... from the URL fragment
    const hash = location.hash || window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate('/login', { replace: true });
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    // Pull any referral/promo codes previously stashed on the register page,
    // and the Founder intent flag stashed by the /subscribe page.
    let referredByCode = null;
    let promoCode = null;
    let founderIntent = false;
    try {
      referredByCode = localStorage.getItem('tradex_ref_code') || null;
      promoCode = localStorage.getItem('tradex_pending_promo') || null;
      founderIntent = localStorage.getItem('tradex_founder_intent') === '1';
    } catch (_e) {
      // localStorage disabled — ignore
    }

    const exchange = async () => {
      try {
        const res = await axios.post(
          `${API}/auth/google/session`,
          {
            session_id: sessionId,
            referred_by_code: referredByCode,
            promo_code: promoCode,
            founder_intent: founderIntent,
          },
          { withCredentials: true }
        );
        // Clean up any transient session data
        try {
          localStorage.removeItem('tradex_ref_code');
          localStorage.removeItem('tradex_pending_promo');
          localStorage.removeItem('tradex_founder_intent');
        } catch (_e) {
          // ignore
        }
        // Clear the hash from the URL so a refresh doesn't retry
        window.history.replaceState(null, '', window.location.pathname + window.location.search);

        // CRITICAL: repopulate AuthContext so the header immediately reflects the signed-in state
        // (otherwise the /me call was skipped on mount because a session_id hash was present).
        try {
          await refreshUser();
        } catch (_e) {
          // best-effort — the ProtectedRoute will re-fetch on the destination page
        }

        // If this Google signup also auto-claimed a Founder slot server-side, celebrate.
        const claimed = res.data?.founder_claimed;
        if (claimed && claimed.slot_number) {
          toast.success(`🏛 Bienvenue Fondateur #${claimed.slot_number}/100 — ${claimed.tier === 'expert' ? 'Expert' : 'Pro'} offert ${claimed.free_months} mois !`);
        } else {
          toast.success(res.data?.is_new_user ? 'Bienvenue sur TRADEX AI 🎉' : 'Reconnexion réussie');
        }
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const backendMsg = err?.response?.data?.detail;
        const friendly = 'Connexion Google échouée — merci de réessayer';
        setError(friendly);
        toast.error(backendMsg ? `${friendly} (${backendMsg})` : friendly);
        setTimeout(() => navigate('/login', { replace: true }), 2500);
      }
    };

    exchange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="auth-callback-screen">
      <div className="text-center space-y-4 max-w-md">
        <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
        <p className="text-lg font-medium text-foreground">
          {error ? 'Redirection...' : 'Finalisation de la connexion...'}
        </p>
        {error && (
          <p className="text-sm text-red-400" data-testid="auth-callback-error">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default AuthCallback;
